/**
 * Meta (Facebook) Pixel — safe wrapper + funnel helpers.
 *
 * The base pixel + PageView fire from `public/index.html`. This file exposes
 * helpers to track funnel events from React components without crashing if
 * the pixel script is blocked (ad-blockers, EU regions, etc.).
 *
 * Pixel ID: 1689588442202168
 *
 * ─── Funnel events mapping ────────────────────────────────────────────────
 * Standard Meta events (high conversion-API weight):
 *   • PageView              → automatic, every page load
 *   • Lead                  → Form 1 popup (30s) submitted
 *   • CompleteRegistration  → Form 2 qualifica completed
 *   • Contact               → click WhatsApp / Telegram CTA
 *   • Schedule              → Calendly slot booked (fired from agent_followup
 *                             or post-booking page when applicable)
 *   • ViewContent           → resort detail page view, organic landing
 *
 * Custom events (audience builders, drop-off analysis, retargeting):
 *   • Form1PopupOpen        → popup appears at 30s (engagement signal)
 *   • Form2StepStart        → starts Form 2 (came from any CTA)
 *   • Form2StepComplete     → step N completed (drop-off funnel)
 *   • VSLProgress25/50/75/100 → video watched milestones
 *   • ScrollDepth25/50/75/90  → landing engagement
 *   • CtaClick              → key landing CTA clicks
 *   • AffiliateLanding      → visitor arrived from /aff/<code>
 *   • OrganicLanding        → visitor arrived from /o/<token>
 *   • DocumentDownload      → brochure / PDF request
 * ──────────────────────────────────────────────────────────────────────────
 *
 * All helpers are wrapped in try/catch — analytics MUST NOT break UX.
 * Each helper is also IDEMPOTENT-friendly: components are expected to call
 * them at most once per unique milestone (use refs/Sets to dedupe).
 */

import { logger } from './logger';

// ─── Internal: safe primitives ────────────────────────────────────────────

/** Safely call window.fbq with a standard event. Never throws. */
export function fbTrack(eventName, params, eventId) {
  if (typeof window === 'undefined') return;
  try {
    if (typeof window.fbq !== 'function') return;
    if (eventId) {
      window.fbq('track', eventName, params || {}, { eventID: eventId });
    } else {
      window.fbq('track', eventName, params || {});
    }
  } catch (err) {
    logger.debug(`fbTrack(${eventName}) failed`, err);
  }
}

/** Custom (non-standard) event. */
export function fbTrackCustom(eventName, params) {
  if (typeof window === 'undefined') return;
  try {
    if (typeof window.fbq !== 'function') return;
    window.fbq('trackCustom', eventName, params || {});
  } catch (err) {
    logger.debug(`fbTrackCustom(${eventName}) failed`, err);
  }
}

// ─── Funnel helpers (standard events) ─────────────────────────────────────

/** Form 1 (popup 30s) submitted → Lead (key event for ad optimization). */
export function trackLead({ leadId, source, country, value } = {}) {
  fbTrack('Lead', {
    content_name: source || 'vsl_gate_form1',
    content_category: 'lead_capture',
    currency: 'EUR',
    value: typeof value === 'number' ? value : 1,
    country,
  }, leadId);
}

/** Form 2 qualifica complete → CompleteRegistration. */
export function trackCompleteRegistration({ leadId, path, country, budget } = {}) {
  fbTrack('CompleteRegistration', {
    content_name: 'qualifica_form2',
    content_category: path || 'unknown',
    country,
    budget_bracket: budget,
    currency: 'EUR',
    value: 5, // higher intent than Lead
    status: true,
  }, leadId);
}

/** WhatsApp / Telegram CTA → Contact. */
export function trackContact({ channel, source } = {}) {
  fbTrack('Contact', {
    content_name: channel || 'unknown_channel',
    content_category: source || 'cta_click',
  });
}

/** Calendly slot booked → Schedule (call when the booking is confirmed). */
export function trackSchedule({ leadId, agentName, branch } = {}) {
  fbTrack('Schedule', {
    content_name: 'calendly_booking',
    content_category: branch || 'unknown',
    agent: agentName,
  }, leadId);
}

/** Resort detail / VSL page view → ViewContent. */
export function trackViewContent({ name, category, contentId } = {}) {
  fbTrack('ViewContent', {
    content_name: name || 'unknown',
    content_category: category || 'page_view',
    content_ids: contentId ? [contentId] : undefined,
  });
}

// ─── Custom funnel events ─────────────────────────────────────────────────

/** Form 1 popup shown at 30s (engagement signal — not yet a lead). */
export function trackForm1PopupOpen({ trigger } = {}) {
  fbTrackCustom('Form1PopupOpen', {
    content_name: 'vsl_gate_form1_popup',
    trigger: trigger || 'auto_30s',
  });
}

/** Form 2 first step started (visitor clicked a qualification CTA). */
export function trackForm2Start({ source } = {}) {
  fbTrackCustom('Form2StepStart', {
    content_name: 'qualifica_form2',
    source: source || 'unknown_cta',
  });
}

/** Form 2 step N completed (used to debug drop-off rates). */
export function trackForm2StepComplete({ step, total, source } = {}) {
  fbTrackCustom('Form2StepComplete', {
    content_name: 'qualifica_form2',
    step,
    total,
    source: source || 'unknown',
  });
}

/** VSL gate video progress (25/50/75/100). Caller must dedupe per threshold. */
export function trackVslProgress(percent) {
  const p = Number(percent) || 0;
  let bucket = null;
  if (p >= 100) bucket = 100;
  else if (p >= 75) bucket = 75;
  else if (p >= 50) bucket = 50;
  else if (p >= 25) bucket = 25;
  if (!bucket) return;
  fbTrackCustom(`VSLProgress${bucket}`, {
    content_name: 'vsl_landing_gate',
    progress: bucket,
  });
}

/** Backwards-compat alias for the older 75% milestone helper. */
export function trackVslComplete() {
  trackVslProgress(75);
}

/** Scroll depth bucket (25/50/75/90). Caller must dedupe per threshold. */
export function trackScrollDepth(percent, pageId) {
  const p = Number(percent) || 0;
  let bucket = null;
  if (p >= 90) bucket = 90;
  else if (p >= 75) bucket = 75;
  else if (p >= 50) bucket = 50;
  else if (p >= 25) bucket = 25;
  if (!bucket) return;
  fbTrackCustom(`ScrollDepth${bucket}`, {
    content_name: pageId || 'landing',
    depth: bucket,
  });
}

/** Click on a primary CTA on the landing or resort pages. */
export function trackCtaClick({ label, source, position } = {}) {
  fbTrackCustom('CtaClick', {
    content_name: label || 'unknown_cta',
    source: source || 'landing',
    position: position || 'unknown',
  });
}

/** Visitor arrived via /aff/<code> (referral attribution). */
export function trackAffiliateLanding({ code, name } = {}) {
  fbTrackCustom('AffiliateLanding', {
    affiliate_code: code,
    affiliate_name: name,
  });
}

/** Visitor arrived via /o/<token> (organic magic link pre-fill). */
export function trackOrganicLanding({ ownerId } = {}) {
  fbTrackCustom('OrganicLanding', { owner_id: ownerId });
}

/** Document / brochure download request. */
export function trackDocumentDownload({ name, leadId } = {}) {
  fbTrackCustom('DocumentDownload', { content_name: name || 'unknown_doc' });
  // Also fire as standard Lead-quality signal for ad optimisation.
  fbTrack('SubmitApplication', { content_name: name || 'document' }, leadId);
}


// ─── Reusable scroll-depth observer (mounts once per page) ────────────────

/**
 * Attach a global scroll-depth tracker to the window. Fires `ScrollDepth25/
 * 50/75/90` AT MOST ONCE per threshold per page-load. Returns a teardown fn.
 *
 * @param {string} pageId — identifier sent with each event (e.g. 'landing').
 * @returns {() => void} cleanup
 */
export function attachScrollDepthTracker(pageId = 'landing') {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => {};
  }
  const sent = new Set();
  const thresholds = [25, 50, 75, 90];

  // Lightweight throttle via rAF
  let scheduled = false;
  const compute = () => {
    scheduled = false;
    try {
      const doc = document.documentElement;
      const body = document.body;
      const scrollTop = window.pageYOffset || doc.scrollTop || body.scrollTop || 0;
      const viewport = window.innerHeight || doc.clientHeight || 0;
      const docHeight = Math.max(
        body.scrollHeight, doc.scrollHeight,
        body.offsetHeight, doc.offsetHeight,
        body.clientHeight, doc.clientHeight,
      );
      const scrollable = Math.max(docHeight - viewport, 1);
      const pct = Math.min(100, Math.round(((scrollTop + viewport) / docHeight) * 100));
      thresholds.forEach((t) => {
        if (pct >= t && !sent.has(t)) {
          sent.add(t);
          trackScrollDepth(t, pageId);
        }
      });
      // Quick exit once all milestones are sent.
      if (sent.size === thresholds.length) {
        teardown();
      }
    } catch (err) {
      logger.debug('scrollDepth compute failed', err);
    }
  };

  const onScroll = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(compute);
  };

  const teardown = () => {
    try {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    } catch (err) { logger.debug('pixel best-effort op failed', err); }
  };

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll, { passive: true });
  // Fire once at mount so short pages that already show >25% trigger early.
  compute();
  return teardown;
}
